import { resolveOperator } from './operator';

/**
 * Classification des transactions TRS pour les onglets
 * (Demandes de paiement / Liens de paiement / Toutes).
 */

const PAYMENT_OPERATION_HINTS = ['payment', 'paiement', 'pmt', 'pay'];

export function normalizeId(value) {
    if (value == null || value === '') return null;
    return String(value).trim();
}

export function isPaymentOperation(operation) {
    const op = String(operation || '').toLowerCase().trim();
    if (!op) return false;
    return PAYMENT_OPERATION_HINTS.some((hint) => op.includes(hint));
}

export function isWithdrawalOperation(operation) {
    const op = String(operation || '').toLowerCase().trim();
    return op.includes('withdraw') || op.includes('retrait') || op.includes('wdl');
}

export function extractTransactionServiceId(transaction) {
    if (!transaction || typeof transaction !== 'object') return null;
    return normalizeId(
        transaction.service ??
        transaction.service_id ??
        transaction.serviceId ??
        transaction.srv
    );
}

export function extractTransactionRef(transaction) {
    if (!transaction || typeof transaction !== 'object') return null;
    return normalizeId(
        transaction.ref ??
        transaction.reference ??
        transaction.transactionId
    );
}

/**
 * @param {Object} options
 * @param {string[]} [options.terminalIds] - IDs des terminaux POS
 * @param {string[]} [options.paymentLinkIds] - IDs des liens de paiement
 * @param {string[]} [options.paymentRequestIds] - IDs des demandes (secours)
 */
export function buildTransactionCategoryIndex({
    terminalIds = [],
    paymentLinkIds = [],
    paymentRequestIds = [],
} = {}) {
    const terminalSet = new Set(terminalIds.map(normalizeId).filter(Boolean));
    const linkSet = new Set(paymentLinkIds.map(normalizeId).filter(Boolean));
    const paymentRequestSet = new Set(paymentRequestIds.map(normalizeId).filter(Boolean));

    return { terminalSet, linkSet, paymentRequestSet };
}

const KIND_LABELS = {
    paymentRequest: 'Paiement au terminal',
    paymentLink: 'Lien de paiement',
    invoice: 'Facture',
    payment: 'Paiement',
    withdrawal: 'Retrait',
    transfer: 'Transfert',
    deposit: 'Dépôt',
};

const POS_TARGET_KINDS = { payment_request: 'paymentRequest', payment_link: 'paymentLink', invoice: 'invoice' };

/**
 * Nature d'une transaction, du signal le plus fiable au plus faible :
 * cible du paiement POS, segment d'opération du code TXN-yyyyMMdd-XXX…, nom d'opération, libellé.
 * @returns {{ type: string, typeLabel: string } | null} null si rien ne permet de trancher
 */
export function resolveTransactionKind(transaction) {
    if (!transaction) return null;
    const kindOf = (type) => ({ type, typeLabel: KIND_LABELS[type] });

    const posKind = POS_TARGET_KINDS[transaction.pos?.target];
    if (posKind) return kindOf(posKind);

    const code = String(transaction.code || '').toUpperCase();
    const opSegment = code.match(/^TXN-\d{8}-([A-Z]{3})/)?.[1];
    if (opSegment === 'WDL') return kindOf('withdrawal');
    if (opSegment === 'PMT') return kindOf('payment');
    if (opSegment === 'TRF') return kindOf('transfer');
    if (opSegment === 'DEP' || opSegment === 'DPT') return kindOf('deposit');

    // Paiement POS (NK-… ou ancien format 2809-A2026-N…) : le libellé est saisi librement
    // (« Paiement de facture REGIDESO »), il ne doit pas décider du type
    if (transaction.publicRef || /^\d{4}-A\d{4}-N\d{6}$/.test(String(transaction.ref || ''))) {
        return kindOf('payment');
    }

    const text = `${transaction.operation || ''} ${transaction.label || ''}`.toLowerCase();
    if (/withdraw|retrait|wdl/.test(text)) return kindOf('withdrawal');
    if (/facture|invoice/.test(text)) return kindOf('invoice');
    if (/lien de paiement|payment.?link/.test(text)) return kindOf('paymentLink');
    if (/transf|trf/.test(text)) return kindOf('transfer');
    if (/payment|paiement|pmt|flash/.test(text)) return kindOf('payment');
    if (/d[ée]p[oô]t|deposit/.test(text)) return kindOf('deposit');

    return null;
}

/**
 * @returns {{ type: string, typeLabel: string }}
 */
export function classifyTransactionType(transaction, index) {
    const resolved = resolveTransactionKind(transaction);
    if (resolved && resolved.type !== 'payment') {
        return resolved;
    }
    const operation = transaction?.operation;
    const serviceId = extractTransactionServiceId(transaction);
    const ref = extractTransactionRef(transaction);

    if (isWithdrawalOperation(operation)) {
        return { type: 'withdrawal', typeLabel: 'Retrait' };
    }

    if (!resolved && !isPaymentOperation(operation)) {
        return { type: 'payment', typeLabel: 'Transaction' };
    }

    const { terminalSet, linkSet, paymentRequestSet } = index || buildTransactionCategoryIndex();

    const isLink =
        (serviceId && linkSet.has(serviceId)) ||
        (ref && linkSet.has(ref));

    if (isLink) {
        return { type: 'paymentLink', typeLabel: 'Lien de paiement' };
    }

    const isRequest =
        (serviceId && terminalSet.has(serviceId)) ||
        (serviceId && paymentRequestSet.has(serviceId)) ||
        (ref && paymentRequestSet.has(ref));

    if (isRequest) {
        return { type: 'paymentRequest', typeLabel: 'Paiement au terminal' };
    }

    // Paiement POS sans service reconnu : plutôt une demande qu'un type générique
    return { type: 'paymentRequest', typeLabel: 'Paiement au terminal' };
}

export function mapTransactionStatus(rawStatus) {
    const s = String(rawStatus || '').toLowerCase();
    if (s === 'completed') return 'completed';
    if (s === 'pending' || s === 'requested' || s === 'approved') return 'pending';
    if (s === 'expired') return 'expired';
    if (s === 'failed' || s === 'canceled' || s === 'cancelled' || s === 'locked') return 'failed';
    return 'failed';
}

export function transformApiTransaction(transaction, index) {
    const transactionDate = transaction.state?.requested_at || transaction.createdAt || new Date();
    const dateObj = new Date(transactionDate);
    const { type, typeLabel } = classifyTransactionType(transaction, index);

    return {
        id: transaction.id,
        transactionId: transaction.code || transaction.publicRef || transaction.ref || transaction.id,
        type,
        typeLabel,
        amount: transaction.amount || transaction.netAmount || 0,
        currency: transaction.currency || 'CDF',
        status: mapTransactionStatus(transaction.status),
        method: resolveOperator({ name: transaction.provider_name || transaction.pos?.provider_name, providerId: transaction.provider_id, phone: transaction.pos?.payer_phone }) || '—',
        provider: resolveOperator({ name: transaction.provider_name || transaction.pos?.provider_name, providerId: transaction.provider_id, phone: transaction.pos?.payer_phone }) || '—',
        account: transaction.pos?.payer_phone || '—',
        date: dateObj.toLocaleDateString('fr-FR'),
        time: dateObj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
        fees: transaction.fees || 0,
        netAmount: transaction.netAmount || transaction.amount || 0,
        description: transaction.label || transaction.description || 'Transaction',
        customer: transaction.client_name || transaction.pos?.payer_name || transaction.pos?.payer_phone || 'Client',
        reference: transaction.code || transaction.publicRef || transaction.ref || transaction.id,
        receiptId: transaction.receiptId || null,
        raw: transaction,
        serviceId: extractTransactionServiceId(transaction),
        operation: transaction.operation,
    };
}

export function partitionTransactionsByCategory(transactions, index) {
    const all = transactions.map((t) => transformApiTransaction(t, index));
    return {
        all,
        paymentRequests: all.filter((t) => t.type === 'paymentRequest'),
        paymentLinks: all.filter((t) => t.type === 'paymentLink'),
        withdrawals: all.filter((t) => t.type === 'withdrawal'),
        deposits: all.filter((t) => t.type === 'deposit'),
    };
}
